import { html, render } from '@cumu/shared';
import { loc_loadingEllipses } from '@cumu/strings';
import { controller } from '@github/catalyst';
import { getLocale } from '../locale';
import ActivitySelectElement, {
  activitySelectChangeEvent
} from './activity-select';
import { dispatchChange } from './util';

const locale = getLocale();

@controller('timesheet-entries')
export default class TimesheetEntriesElement extends HTMLElement {
  get pay_period_activity_id() {
    return this.getAttribute('pay_period_activity_id');
  }
  set pay_period_activity_id(value: string | null) {
    if (value) {
      this.setAttribute('pay_period_activity_id', value);
    } else {
      this.removeAttribute('pay_period_activity_id');
    }
  }

  connectedCallback() {
    addEventListener(activitySelectChangeEvent, this);
  }

  disconnectedCallback() {
    removeEventListener(activitySelectChangeEvent, this);
  }

  handleEvent(event: Event) {
    const { type, target } = event;
    switch (type) {
      case activitySelectChangeEvent: {
        if (
          !(target instanceof ActivitySelectElement) ||
          !target.period ||
          target.activity_id === this.pay_period_activity_id
        ) {
          return;
        }

        this.pay_period_activity_id = target.activity_id;

        if (!this.pay_period_activity_id) {
          this.innerHTML = '';
          return;
        }

        const url = new URL(this.getAttribute('src')!, location.origin);
        url.searchParams.set(
          'pay_period_activity_id',
          this.pay_period_activity_id
        );

        this.innerHTML = render(
          html`<include-fragment
            src="${url.pathname + url.search}"
            accept="text/fragment+html"
            class="d-flex gap-2 flex-items-center flex-justify-center py-12 color-fg-muted color-bg-inset rounded"
            data-target="activity-select.preview"
            role="status"
          >
            <span class="spinner-border"></span>
            <span>${loc_loadingEllipses[locale]}</span>
          </include-fragment>`
        );
        return;
      }

      default:
        throw new Error(`Unhandled event: ${event.type}`);
    }
  }

  appendEntryDeletionComment(event: Event) {
    const deletionComment =
      event.currentTarget instanceof HTMLElement &&
      event.currentTarget.dataset.deletionComment;
    if (!deletionComment) {
      return;
    }
    const form = this.closest('form');
    const comment = form?.elements.namedItem('comment_body');
    if (!(comment instanceof HTMLTextAreaElement)) {
      return;
    }
    comment.value += `${comment.value ? '\n' : ''}${deletionComment}`;
    dispatchChange(comment);
  }
}

declare global {
  interface Window {
    TimesheetEntriesElement: typeof TimesheetEntriesElement;
  }
  interface HTMLElementTagNameMap {
    'timesheet-entries': TimesheetEntriesElement;
  }
}
